import type { ApiResponse, PaginatedResponse } from '@klaro7/shared-types';

import api from '@/lib/api';

export interface AdminProfessional {
  id: string;
  title: string;
  specializations: string[];
  experience: number;
  education?: string;
  certifications?: string[];
  hourlyRate?: number;
  costPerMinute?: number;
  currency?: string;
  videoUrl?: string;
  profileDescription?: string;
  availability?: {
    isAvailable: boolean;
    workingHours?: { dayOfWeek: number; startTime: string; endTime: string }[];
    timezone: string;
    notes?: string | null;
  };
  serviceChannels?: {
    chat: boolean;
    audioCall: boolean;
    videoCall: boolean;
  };
  verificationStatus: 'pending' | 'in_review' | 'verified' | 'rejected';
  /** Latest questionnaire session status (admin list only) */
  questionnaireStatus?: 'in_progress' | 'completed' | 'approved' | 'rejected';
  /** Only present while pending: requirements still blocking submit-for-review */
  missingForReview?: string[];
  /** Quanti clienti hanno il consulente tra i preferiti. */
  favoriteCount?: number;
  reviewNotes?: string;
  verifiedAt?: string;
  rejectionReason?: string;
  adminNotes?: string;
  rating?: number;
  reviewCount?: number;
  onlineStatus: 'online' | 'busy' | 'offline' | 'paused';
  isSuspended?: boolean;
  suspension?: {
    reason: string;
    suspendedAt: string;
    suspendedByName: string;
  } | null;
  suspensionHistory?: Array<{
    action: 'suspended' | 'reactivated';
    at: string;
    byName: string;
    reason?: string;
  }>;
  forcedOffline?: boolean;
  forcedOfflineInfo?: {
    reason: string;
    at: string;
    forcedByName: string;
  } | null;
  forcedOfflineHistory?: Array<{
    action: 'forced' | 'cleared';
    at: string;
    byName: string;
    reason?: string;
  }>;
  /** Payout IBAN — write-once, returned only to the super-admin */
  iban?: string;
  ibanSetAt?: string;
  userId: {
    /** Id dell'utente popolato (toJSON → id; _id per compatibilità). */
    id?: string;
    _id?: string;
    email: string;
    profile: {
      firstName: string;
      lastName: string;
      artFirstName?: string;
      artLastName?: string;
      avatar?: string;
      phone?: string;
      pendingPhone?: string;
      phoneVerified?: boolean;
      phoneVerifiedAt?: string;
      bio?: string;
    };
  };
  createdAt: string;
  updatedAt: string;
}

export interface AdminProfessionalQueryParams {
  page?: number;
  limit?: number;
  sortBy?: string;
  sortOrder?: 'asc' | 'desc';
  /** 'approved' is virtual: questionnaire approved, consultant not yet published */
  verificationStatus?: 'pending' | 'in_review' | 'verified' | 'rejected' | 'approved';
  onlineStatus?: 'online' | 'busy' | 'offline' | 'paused';
  search?: string;
}

export async function getAdminProfessionals(
  params?: AdminProfessionalQueryParams
): Promise<PaginatedResponse<AdminProfessional>> {
  const response = await api.get<PaginatedResponse<AdminProfessional>>(
    '/professionals',
    { params }
  );
  return response.data;
}

export async function getAdminProfessionalById(id: string): Promise<AdminProfessional> {
  const response = await api.get<ApiResponse<AdminProfessional>>(
    `/professionals/${id}`
  );
  return response.data.data;
}

export async function verifyProfessional(
  id: string,
  data: { verificationStatus: 'verified' | 'rejected'; rejectionReason?: string; adminNotes?: string }
): Promise<AdminProfessional> {
  const response = await api.patch<ApiResponse<AdminProfessional>>(
    `/professionals/${id}/verify`,
    data
  );
  return response.data.data;
}

/** Re-send the access-credentials email to an already-approved professional. */
export async function resendCredentials(id: string): Promise<void> {
  await api.post(`/professionals/${id}/resend-credentials`);
}

export async function suspendProfessional(id: string, reason: string): Promise<AdminProfessional> {
  const res = await api.post<ApiResponse<AdminProfessional>>(`/professionals/${id}/suspend`, { reason });
  return res.data.data;
}

export async function reactivateProfessional(id: string): Promise<AdminProfessional> {
  const res = await api.post<ApiResponse<AdminProfessional>>(`/professionals/${id}/reactivate`);
  return res.data.data;
}

export async function forceProfessionalOffline(id: string, reason: string): Promise<AdminProfessional> {
  const res = await api.post<ApiResponse<AdminProfessional>>(`/professionals/${id}/force-offline`, { reason });
  return res.data.data;
}

export async function clearProfessionalForcedOffline(id: string): Promise<AdminProfessional> {
  const res = await api.post<ApiResponse<AdminProfessional>>(`/professionals/${id}/clear-force-offline`);
  return res.data.data;
}

/** Deliver login credentials while the profile stays pending/invisible. */
export async function issueCredentials(id: string): Promise<void> {
  await api.post(`/professionals/${id}/issue-credentials`);
}

/** Bounce an in_review profile back to the consultant with notes. */
export async function requestChanges(id: string, notes: string): Promise<void> {
  await api.post(`/professionals/${id}/request-changes`, { notes });
}

/** Bring a rejected professional back to pending (reversible rejection). */
export async function reopenProfessional(id: string): Promise<void> {
  await api.post(`/professionals/${id}/reopen`);
}

/**
 * Set the per-minute rate from the admin console. It is the only way out for a
 * profile published without one: the consultant's own editor is behind their
 * login, and without a rate the availability gate keeps them offline.
 */
export async function updateProfessionalRate(
  id: string,
  costPerMinute: number
): Promise<AdminProfessional> {
  const res = await api.put<ApiResponse<AdminProfessional>>(`/professionals/${id}`, {
    costPerMinute,
  });
  return res.data.data;
}
